import React from 'react'
import ReactDOM from 'react-dom'
import classnames from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import useClickOutsideListener from '@Utils/UseClickOutsideListener'
import getFullName from '@Utils/GetFullName'
import AccountsList from './AccountsList'
import Menu from './Menu'

import {
  openAccountPopup,
  closeAccountPopup,
} from '@SiteModules/Popup/actions'

import {
  logout,
  createCustomerPortalSession,
  setUseLightTheme,
  resetUseLightTheme,
} from '@SiteModules/User/actions'

import {
  setCurrentAccount,
} from '@SiteModules/Account/actions'

import {
  getAccounts,
  getCurrentAccountId,
  getUserIsAccountAdmin,
  getCurrentAccountHasCurrentSubscription,
  getCurrentAccountHasEnterpriseOrCustomSubscription,
} from '@SiteModules/Account/selectors'

import {
  getUserName,
  getUserLastName,
  getUserEmail,
  getUserAvatarUrl,
  getUserId,
} from '@SiteModules/User/selectors'

import {
  ISetCurrentAccountPayload,
} from '@SiteModules/Account/types'

import './AccountPopup.scss'

interface AccountPopupProps {
  readonly accountPopupIsOpen: boolean
}

const AccountPopup: React.FC<AccountPopupProps> = props => {
  const dispatch = useDispatch()

  const userName = useSelector(getUserName)
  const userLastName = useSelector(getUserLastName)
  const userEmail = useSelector(getUserEmail)
  const userAvatarUrl = useSelector(getUserAvatarUrl)
  const userId = useSelector(getUserId)
  const accounts = useSelector(getAccounts)
  const currentAccountId = useSelector(getCurrentAccountId)
  const userIsAccountAdmin = useSelector(getUserIsAccountAdmin)
  const currentAccountHasCurrentSubscription = useSelector(getCurrentAccountHasCurrentSubscription)
  const currentAccountHasEnterpriseOrCustomSubscription = useSelector(getCurrentAccountHasEnterpriseOrCustomSubscription)
  const userFullName = getFullName(userName, userLastName)
  const userHasName = userFullName.length > 0
  const lastDigitOfUserId = userId % 10

  const setAccountPopupOpen = (newIsAccountPopupOpen: boolean) => {
    if (newIsAccountPopupOpen) {
      dispatch(openAccountPopup())
    } else {
      dispatch(closeAccountPopup())
    }
  }

  const handleCloseAccountPopup = () => {
    dispatch(closeAccountPopup())
  }

  useClickOutsideListener('AccountPopup__element', setAccountPopupOpen, props.accountPopupIsOpen)

  const handleLogout = () => {
    dispatch(logout({ forwardingUrl: '/users/sign_in' }))
  }

  const handleCreateCustomerPortalSession = () => {
    dispatch(createCustomerPortalSession({ accountId: currentAccountId }))
  }

  const handleSetCurrentAccount = (payload: ISetCurrentAccountPayload) => {
    dispatch(setCurrentAccount(payload))
  }

  const handleSetUseLightTheme = () => {
    dispatch(setUseLightTheme())
    dispatch(closeAccountPopup())
  }

  const handleResetUseLightTheme = () => {
    dispatch(resetUseLightTheme())
    dispatch(closeAccountPopup())
  }

  return ReactDOM.createPortal(
    (
      <div
        className={classnames(
          'AccountPopup AccountPopup__element',
          { 'AccountPopup__open': props.accountPopupIsOpen,
            'AccountPopup__closed': !props.accountPopupIsOpen }
        )}
      >
        {userAvatarUrl ? (
          <img
            src={userAvatarUrl}
            alt={userFullName}
            className="AccountPopup__user-avatar-base AccountPopup__user-avatar"
          />
        ) : (
          <div
            className={classnames(
              'AccountPopup__user-avatar-base',
              'AccountPopup__user-avatar-placeholder',
              `AccountPopup__user-avatar-placeholder-background-${lastDigitOfUserId}`
            )}
          >
            {userHasName ? userFullName.charAt(0) : userEmail.charAt(0)}
          </div>
        )}
        {userHasName && (
          <div className="AccountPopup__user-name">
            {userFullName}
          </div>
        )}
        <div className="AccountPopup__user-email">
          {userEmail}
        </div>
        <div className="AccountPopup__line" />
        <AccountsList
          accounts={accounts}
          currentAccountId={currentAccountId}
          setCurrentAccount={handleSetCurrentAccount}
        />
        <div className="AccountPopup__line" />
        <Menu
          userIsAccountAdmin={userIsAccountAdmin}
          logout={handleLogout}
          createCustomerPortalSession={handleCreateCustomerPortalSession}
          currentAccountHasCurrentSubscription={currentAccountHasCurrentSubscription}
          currentAccountHasEnterpriseOrCustomSubscription={currentAccountHasEnterpriseOrCustomSubscription}
          closeAccountPopup={handleCloseAccountPopup}
          handleSetUseLightTheme={handleSetUseLightTheme}
          handleResetUseLightTheme={handleResetUseLightTheme}
        />
      </div>
    ),
    document.getElementById('site-container')!
  )
}

export default AccountPopup
